<template>
  <div v-if="isLoading">
    <Spinner />
  </div>
  <div v-else>
    <Table>
      <TableHeader>
        <TableRow class="border-neutral-700">
          <TableHead>User</TableHead>
          <TableHead class="w-[240px]">Role</TableHead>
          <TableHead class="text-right" />
        </TableRow>
      </TableHeader>
      <TableBody v-if="members.length">
        <TableRow
          v-for="member in members"
          :key="member.id"
          class="border-neutral-700"
        >
          <TableCell class="font-medium">
            <div class="flex gap-3 items-center">
              <Avatar
                :rounded="true"
                :color="member.user.color"
                :src="member.user.avatar_url || member.user.gravatar_url"
                :alt="member.user.short_name"
                :initials="member.user.short_name"
                size="large"
              />
              <div>
                <div class="text-base font-semibold text-white">
                  {{ member.user.full_name }}
                </div>
                <div>{{ member.user.email }}</div>
              </div>
            </div>
          </TableCell>
          <TableCell class="text-right">
            <Protect :permissions="[SYS_PERMISSIONS.SYS_MEMBERSHIPS_MANAGE]">
              <Select
                :model-value="member.role.id.toString()"
                :disabled="!member.can_change_role || isChangeRolePending"
                @update:model-value="onChangeRole($event, member.id)"
              >
                <SelectTrigger class="!w-40 text-left">
                  <SelectValue :placeholder="member.role.name" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="role in roles"
                    :key="role.id"
                    :value="role.id.toString()"
                  >
                    {{ role.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <template #fallback>
                <span>{{ member.role.name }}</span>
              </template>
            </Protect>
          </TableCell>
          <TableCell class="text-right">
            <Protect :permissions="[SYS_PERMISSIONS.SYS_MEMBERSHIPS_DELETE]">
              <DropdownMenu>
                <DropdownMenuTrigger as="span">
                  <IconButton variant="ghost" size="sm">
                    <MoreHorizontal />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <Protect
                    :permissions="[SYS_PERMISSIONS.SYS_MEMBERSHIPS_DELETE]"
                  >
                    <DropdownMenuItem
                      :disabled="!member.is_deletable"
                      @click="onDeleteMembership(member)"
                    >
                      Remove
                    </DropdownMenuItem>
                  </Protect>
                </DropdownMenuContent>
              </DropdownMenu>
            </Protect>
          </TableCell>
        </TableRow>
      </TableBody>
      <TableBody v-else>
        <TableRow class="border-neutral-700">
          <TableCell class="text-center" colspan="4">
            No members found
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
    <AlertDialog v-model:open="deleteDialogOpen">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
        </AlertDialogHeader>
        <div class="px-7">
          <AlertDialogDescription>
            This action cannot be undone.
          </AlertDialogDescription>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel as-child>
            <Button variant="ghost">Cancel</Button>
          </AlertDialogCancel>
          <AlertDialogAction as-child>
            <Button
              variant="danger"
              :disabled="isDeletePending"
              @click="confirmDeleteMembership(membershipToDelete.id)"
            >
              Remove
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import Protect from '@/hoc/Protect.vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { MoreHorizontal } from 'lucide-vue-next';
import { Button } from '@ui/components/button';
import { Avatar } from '@ui/components/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/components/table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@ui/components/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/components/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/components/alert-dialog';
import { IconButton } from '@ui/components/icon-button';
import Spinner from '@ui/components/Spinner.vue';
import { toast } from 'vue-sonner';
import requests from '@vuex/requests.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import { queries } from '@/queries/index.js';
import { useStore } from 'vuex';

const { isLoading: isLoadingMembers, data: members } = useQuery(
  queries.workspace.members
);

const { isLoading: isLoadingRoles, data: roles } = useQuery(
  queries.workspace.roles
);

const isLoading = isLoadingMembers || isLoadingRoles;

const queryClient = useQueryClient();

const { isPending: isDeletePending, mutate: deleteMembership } = useMutation({
  mutationFn: async payload => requests.memberships.delete({ ...payload }),
  onSuccess: async () => {
    queryClient.invalidateQueries({
      queryKey: queries.workspace.members.queryKey,
    });
    toast.success('Member was successfully removed!');
  },
  onError: async err => {
    toast.error(err.message);
  },
});

const membershipToDelete = ref(null);
const deleteDialogOpen = ref(null);

function onDeleteMembership(membership) {
  membershipToDelete.value = membership;
  deleteDialogOpen.value = true;
}

const store = useStore();

const workspaceId = computed(() => store.getters['workspace/getId']);

function confirmDeleteMembership(membershipId) {
  deleteMembership({
    workspaceId: workspaceId.value,
    id: membershipId,
  });
}

const { isPending: isChangeRolePending, mutate: changeRole } = useMutation({
  mutationFn: async payload => requests.memberships.update({ ...payload }),
  onSuccess: async () => {
    queryClient.invalidateQueries({
      queryKey: queries.workspace.members.queryKey,
    });
    toast.success('Role was successfully updated!');
  },
  onError: async err => {
    toast.error(err.message);
  },
});

function onChangeRole(value, membershipId) {
  changeRole({
    workspaceId: workspaceId.value,
    id: membershipId,
    role: value,
  });
}
</script>
